import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Organization } from 'js/models'
import { MAX_REQUEST_SIZE } from 'js/services/api'
import { ListControlRunsParams } from 'js/services/controlRuns'
import { listOrganizations } from 'js/services/organization'
import { SelfUser } from 'js/utils/helpers'
import Paths from 'js/utils/paths'
import VQCPermissions from 'js/utils/permissions'

import ControlRunsTable from './ControlRunsTable'
import ControlRunMonthsTable from './ControlRunMonthsTable'
import { Typeahead as TypeaheadComponent } from 'react-bootstrap-typeahead'
import Typeahead from 'react-bootstrap-typeahead/types/core/Typeahead'
import { Option } from 'react-bootstrap-typeahead/types/types'

export enum TimeRange {
  lastTwelveMonths = 'Last 12 Months',
  lastTwoYears = 'Last 2 Years',
  allTime = 'All Time',
}

export default function ControlRunsTableWrapper(props: {
  listParams?: ListControlRunsParams
  showOrgFilter?: boolean
}): React.ReactElement {
  const { listParams, showOrgFilter } = props
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selfUser = useContext(SelfUser)
  const [organizationList, setOrganizationList] = useState<Organization[]>([])
  const [orgId, setOrgId] = useState<number | null>()
  const initialYearQuery = searchParams.get('year')
  const initialMonthQuery = searchParams.get('month')
  const [year, setYear] = useState<number | null>(
    parseInt(initialYearQuery ?? '') || new Date().getFullYear()
  )
  const [month, setMonth] = useState<number | null>(
    parseInt(initialMonthQuery ?? '') || new Date().getMonth() + 1
  )
  const [timeRange, setTimeRange] = useState<TimeRange>(
    TimeRange.lastTwelveMonths
  )
  const [showMonthList, setShowMonthList] = useState(false)

  useEffect(() => {
    if (organizationList.length === 0) return
    const orgSearchParam = searchParams.get('organization')
    if (orgSearchParam) {
      const parsed = parseInt(orgSearchParam)
      const isValidOrg = organizationList.some(org => org.id === parsed)
      setOrgId(isValidOrg ? parsed : selfUser.organization ?? null)
    } else {
      setOrgId(listParams?.organization || (selfUser.organization ?? null))
    }
  }, [organizationList])

  const isElitechAdmin = VQCPermissions.isElitechAdmin(selfUser)
  const canSubmitData =
    VQCPermissions.canEditCustomerData(selfUser) ||
    (selfUser.organization && orgId && selfUser.organization == orgId)

  useEffect(() => {
    listOrganizations({ count: MAX_REQUEST_SIZE }).then(r =>
      setOrganizationList(r.results)
    )
  }, [])
  
  const selectedOrgFilter = useMemo(() => {
    return organizationList.find(o => o.id === orgId)
  }, [orgId, organizationList])

  const typeaheadRef = useRef<Typeahead>(null)
  const [inputText, setInputText] = useState<string>('')

  const sortedOrgOptions = useMemo(() => {
    if (!inputText) return organizationList

    return [...organizationList].sort((a, b) => {
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()
      const input = inputText.toLowerCase()

      const aStarts = aName.startsWith(input)
      const bStarts = bName.startsWith(input)

      if (aStarts && !bStarts) return -1
      if (!aStarts && bStarts) return 1

      const aIncludes = aName.includes(input)
      const bIncludes = bName.includes(input)

      if (aIncludes && !bIncludes) return -1
      if (!aIncludes && bIncludes) return 1

      return aName.localeCompare(bName)
    })
  }, [organizationList, inputText])

  return (
    <>
      <div className='table-top-bar'>
        <div className='table-top-section'>
          {!showMonthList && month && year ? (
            <Form.Group className='form-group'>
              <Form.Label>Month</Form.Label>
              <Form.Control
                type='month'
                onChange={(e): void => {
                  if (e.target.value) {
                    const [newYear, newMonth] = e.target.value.split('-')
                    setSearchParams({
                      year: newYear,
                      month: newMonth,
                      ...(orgId && listParams?.organization
                        ? { organization: orgId.toString() }
                        : {}),
                    })
                    setYear(parseInt(newYear))
                    setMonth(parseInt(newMonth))
                  }
                }}
                defaultValue={`${year}-${month.toString().padStart(2, '0')}`}
              />
            </Form.Group>
          ) : (
            <Form.Group className='form-group'>
              <Form.Label>Time Range</Form.Label>
              <Form.Select
                value={timeRange}
                onChange={(e): void =>
                  setTimeRange(e.target.value as TimeRange)
                }
              >
                {Object.values(TimeRange).map(timeRange => (
                  <option key={timeRange} value={timeRange}>
                    {timeRange}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {isElitechAdmin && showOrgFilter && (
            <Form.Group className='ps-3 form-group'>
              <Form.Label>Organization</Form.Label>
              <TypeaheadComponent
                id='choose-org-control-runs'
                ref={typeaheadRef}
                selected={selectedOrgFilter ? [selectedOrgFilter] : []}
                options={sortedOrgOptions}
                placeholder=' -- Select an Organization -- '
                labelKey={(option: Option): string => {
                  const org = option as Organization
                  return org.name
                }}
                onInputChange={(text): void => setInputText(text)}
                onChange={(selected: Option[]): void => {
                  const newOrg = selected[0] as Organization
                  setOrgId(newOrg?.id)
                  if (newOrg?.id) {
                    const updatedParams = new URLSearchParams(
                      searchParams.toString()
                    )
                    updatedParams.set('organization', newOrg.id.toString())
                    setSearchParams(updatedParams)
                  }
                }}
                filterBy={(): boolean => true} // show all options
                onBlur={(): void => {
                  // Reset input if nothing selected
                  if (!selectedOrgFilter && typeaheadRef.current) {
                    typeaheadRef.current.clear()
                    setInputText('')
                  } else if (selectedOrgFilter) {
                    // Ensure inputText reflects the selected option on blur
                    setInputText(selectedOrgFilter.name)
                  }
                }}
              />
            </Form.Group>
          )}
          {!showMonthList && canSubmitData && (
            <Button
              onClick={(): void | Promise<void> =>
                navigate(
                  orgId
                    ? Paths.getSubmitDataLink(
                        orgId,
                        year || undefined,
                        month || undefined
                      )
                    : Paths.submitData
                )
              }
              variant='primary'
              style={{ height: 'fit-content', marginLeft: '1rem' }}
            >
              Enter New Data
            </Button>
          )}
        </div>
      </div>

      {!showMonthList && month && year ? (
        <ControlRunsTable
          orgId={orgId}
          startMonth={month}
          startYear={year}
          setShowMonthList={setShowMonthList}
          updateOrgParam={!listParams?.organization}
        />
      ) : (
        <ControlRunMonthsTable
          timeRange={timeRange}
          orgId={orgId}
          setShowMonthList={setShowMonthList}
          setYear={setYear}
          setMonth={setMonth}
          updateOrgParam={!listParams?.organization}
        />
      )}
    </>
  )
}
